import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Package, Users, ShoppingCart, LogOut, LayoutDashboard, Loader2, Mail, Newspaper, Menu, X, Tag, Home, Truck, Percent } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import AdminProducts from "@/components/admin/AdminProducts";
import AdminOrders from "@/components/admin/AdminOrders";
import AdminCustomers from "@/components/admin/AdminCustomers";
import AdminStats from "@/components/admin/AdminStats";
import AdminContacts from "@/components/admin/AdminContacts";
import AdminSubscriptions from "@/components/admin/AdminSubscriptions";
import AdminCategories from "@/components/admin/AdminCategories";
import AdminShipping from "@/components/admin/AdminShipping";
import AdminPromoCodes from "@/components/admin/AdminPromoCodes";

type TabType = "dashboard" | "products" | "categories" | "orders" | "customers" | "contacts" | "subscriptions" | "shipping" | "promos";

const AdminDashboard = () => {
  const { isAuthenticated, isAdmin, isLoading, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("dashboard");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { data: contactCount = 0 } = useQuery({
    queryKey: ["admin-contacts-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("contact_submissions")
        .select("*", { count: "exact", head: true })
        .eq("is_read", false);
      if (error) throw error;
      return count || 0;
    },
    enabled: isAuthenticated && isAdmin,
    refetchInterval: 30000,
  });

  const { data: pendingOrderCount = 0 } = useQuery({
    queryKey: ["admin-pending-orders-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("orders")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");
      if (error) throw error;
      return count || 0;
    },
    enabled: isAuthenticated && isAdmin,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (!isLoading && (!isAuthenticated || !isAdmin)) {
      navigate("/admin");
    }
  }, [isAuthenticated, isAdmin, isLoading, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) return null;

  const tabs = [
    { id: "dashboard" as const, label: "Dashboard", icon: LayoutDashboard, badge: 0 },
    { id: "products" as const, label: "Products", icon: Package, badge: 0 },
    { id: "categories" as const, label: "Categories", icon: Tag, badge: 0 },
    { id: "orders" as const, label: "Orders", icon: ShoppingCart, badge: pendingOrderCount },
    { id: "customers" as const, label: "Customers", icon: Users, badge: 0 },
    { id: "contacts" as const, label: "Contacts", icon: Mail, badge: contactCount },
    { id: "subscriptions" as const, label: "Subscriptions", icon: Newspaper, badge: 0 },
    { id: "shipping" as const, label: "Shipping", icon: Truck, badge: 0 },
    { id: "promos" as const, label: "Promo Codes", icon: Percent, badge: 0 },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-serif text-gradient-gold arabic">بَيْتُ الطِّيبِ</h2>
          <p className="text-[10px] tracking-[0.15em] text-muted-foreground uppercase">Admin</p>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 text-foreground"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-40 bg-background/95 backdrop-blur-sm pt-16"
          >
            <nav className="flex flex-col p-4 gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-3 px-4 py-2 rounded-sm w-full text-left transition-colors text-sm ${
                    activeTab === tab.id
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="flex-1">{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className="ml-auto bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
              <div className="mt-4 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  className="w-full mb-3"
                  onClick={() => navigate("/")}
                >
                  <Home className="w-4 h-4" />
                  Go to Home
                </Button>
                <div className="p-4 bg-muted rounded-sm mb-4">
                  <p className="text-sm text-muted-foreground">Logged in as</p>
                  <p className="font-medium truncate text-sm">{user?.email}</p>
                </div>
                <Button variant="elegant" className="w-full" onClick={handleSignOut}>
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-full w-64 bg-card border-r border-border p-6 z-50">
        <div className="mb-10 shrink-0">
          <h2 className="text-xl font-serif text-gradient-gold arabic">بَيْتُ الطِّيبِ</h2>
          <p className="text-xs tracking-[0.2em] text-muted-foreground uppercase">Admin Panel</p>
        </div>

        <nav className="space-y-1 flex-1 overflow-y-auto min-h-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-4 py-2 rounded-sm w-full text-left transition-colors text-sm ${
                activeTab === tab.id
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="flex-1">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="ml-auto bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="shrink-0 pt-4 border-t border-border mt-4">
          <Button
            variant="outline"
            className="w-full mb-3"
            onClick={() => navigate("/")}
          >
            <Home className="w-4 h-4" />
            Go to Home
          </Button>
          <div className="p-4 bg-muted rounded-sm mb-4">
            <p className="text-sm text-muted-foreground">Logged in as</p>
            <p className="font-medium truncate">{user?.email}</p>
          </div>
          <Button variant="elegant" className="w-full" onClick={handleSignOut}>
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 pt-20 lg:pt-8 lg:p-8">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {activeTab === "dashboard" && <AdminStats />}
          {activeTab === "products" && <AdminProducts />}
          {activeTab === "categories" && <AdminCategories />}
          {activeTab === "orders" && <AdminOrders />}
          {activeTab === "customers" && <AdminCustomers />}
          {activeTab === "contacts" && <AdminContacts />}
          {activeTab === "subscriptions" && <AdminSubscriptions />}
          {activeTab === "shipping" && <AdminShipping />}
          {activeTab === "promos" && <AdminPromoCodes />}
        </motion.div>
      </main>
    </div>
  );
};

export default AdminDashboard;
